import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, CreditCard, Star, X, Shield, Users, Briefcase, Heart, ChevronRight, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import { MemberComparisonTable, ConseillerComparisonTable } from './SubscriptionComparisonTables';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionManagement = () => {
  const { getAuthHeader, user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('member'); // 'member' or 'conseiller'
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/billing/plans`),
        axios.get(`${BACKEND_URL}/api/billing/subscription`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      
      setPlans(plansRes.data);
      setCurrentSubscription(subRes.data);
      
      // Déterminer l'onglet actif selon le rôle de l'utilisateur
      if (user?.role === 'conseiller_ess') {
        setActiveTab('conseiller');
      }
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/billing/subscription`,
        { plan: planId },
        { headers: getAuthHeader() }
      );
      
      toast.success('Abonnement activé avec succès !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la souscription');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/billing/subscription`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Abonnement annulé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Helper pour déterminer si un plan est actif
  const isPlanActive = (planId) => {
    return currentSubscription?.subscription?.plan === planId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
      {/* Bouton Retour */}
      <BackButton label="Retour" />

      {/* En-tête */}
      <div className="text-center space-y-3">
        <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 font-semibold">
          Offres Premium COPPAM
        </Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Choisissez votre formule
        </h1>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          Des abonnements flexibles avec crédits offerts pour accompagner votre croissance
        </p>
      </div>

      {/* Abonnement actuel */}
      {currentSubscription?.subscription && (
        <Card className="border-blue-500 border-2 bg-gradient-to-br from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-blue-600 fill-blue-600" />
              Votre Abonnement Actuel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Formule</p>
                <Badge className="bg-blue-600 text-white text-base px-3 py-1 font-semibold">
                  {currentSubscription.plan_details?.name || currentSubscription.subscription.plan}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Période</p>
                <p className="text-sm font-semibold">
                  {new Date(currentSubscription.subscription.current_period_start).toLocaleDateString()} 
                  {' → '}
                  {new Date(currentSubscription.subscription.current_period_end).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Crédits mensuels</p>
                <p className="text-lg font-bold text-blue-600">
                  {currentSubscription.subscription.monthly_credits_allocated} crédits
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleCancelSubscription}
                disabled={currentSubscription.subscription.cancel_at_period_end}
                className="flex-1"
              >
                {currentSubscription.subscription.cancel_at_period_end 
                  ? '⏳ Annulation planifiée' 
                  : 'Annuler l\'abonnement'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toggle Cycle de facturation */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              billingCycle === 'annual'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Annuel
            <Badge variant="success" className="bg-green-600 text-white text-xs font-semibold">
              2 mois offerts
            </Badge>
          </button>
        </div>
      </div>

      {/* Onglets Membres / Conseillers */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
          <TabsTrigger value="member" className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Membres (Entreprises)
          </TabsTrigger>
          <TabsTrigger value="conseiller" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Conseillers ESS
          </TabsTrigger>
        </TabsList>

        {/* Onglet Membres */}
        <TabsContent value="member" className="space-y-8 mt-6">
          {/* Grille des Plans Membres */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {plans?.member_plans && Object.entries(plans.member_plans)
              .filter(([planId]) => 
                billingCycle === 'monthly' 
                  ? !planId.includes('_annual')
                  : planId.includes('_annual')
              )
              .map(([planId, plan]) => {
                const baseId = planId.replace('_annual', '');
                const isPro = baseId.includes('pro') && !baseId.includes('premium');
                const isPremium = baseId.includes('premium');
                const isActive = isPlanActive(planId);
                
                return (
                  <Card 
                    key={planId}
                    className={`relative transition-all hover:shadow-xl ${
                      isPro ? 'border-blue-500 border-2 shadow-lg transform scale-105' : ''
                    } ${
                      isPremium ? 'border-purple-500 border-2 shadow-xl bg-gradient-to-br from-purple-50 to-blue-50' : ''
                    } ${
                      isActive ? 'ring-2 ring-green-500' : ''
                    }`}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                        <Badge className="bg-blue-600 text-white shadow-md font-semibold">⭐ POPULAIRE</Badge>
                      </div>
                    )}
                    {isPremium && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                        <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md font-semibold">💎 MEILLEUR ROI</Badge>
                      </div>
                    )}
                    {isActive && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-green-600 text-white font-semibold">✓ Actif</Badge>
                      </div>
                    )}
                    
                    <CardHeader className="pb-4">
                      <div className="space-y-1">
                        <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
                        {plan.target && (
                          <p className="text-xs text-gray-500 font-medium">{plan.target}</p>
                        )}
                      </div>
                      <div className="pt-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-blue-600">{plan.price}€</span>
                          <span className="text-gray-500">/{billingCycle === 'monthly' ? 'mois' : 'an'}</span>
                        </div>
                        {billingCycle === 'annual' && (
                          <p className="text-xs text-green-600 font-semibold mt-1">
                            ≈ {Math.round(plan.price / 10)}€/mois • 2 mois offerts
                          </p>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Bloc Crédits Offerts */}
                      {plan.monthly_credits > 0 && (
                        <div className={`p-4 rounded-lg border-2 ${
                          isPremium 
                            ? 'bg-gradient-to-br from-purple-100 to-pink-100 border-purple-300'
                            : 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-300'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Heart className="h-5 w-5 text-red-500 fill-red-500" />
                            <span className="font-bold text-sm">Bonus Mensuel</span>
                          </div>
                          <p className="text-2xl font-bold text-blue-900">
                            {plan.monthly_credits} crédits
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            Valeur : {plan.monthly_credits}€/mois
                          </p>
                          {isPro && (
                            <div className="mt-2 pt-2 border-t border-blue-200">
                              <p className="text-xs font-semibold text-green-700">
                                💡 Coût net réel : {plan.price - plan.monthly_credits}€/mois
                              </p>
                            </div>
                          )}
                          {isPremium && (
                            <div className="mt-2 pt-2 border-t border-purple-200">
                              <p className="text-sm font-bold text-purple-700">
                                💎 GAIN NET : +{plan.monthly_credits - plan.price}€/mois !
                              </p>
                              <p className="text-xs text-gray-600">Vous êtes payé pour être abonné !</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Liste des Fonctionnalités */}
                      <ul className="space-y-2.5">
                        {plan.features && plan.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                        {plan.features && plan.features.length > 6 && (
                          <li className="text-xs text-blue-600 font-medium pl-6">
                            + {plan.features.length - 6} autres avantages...
                          </li>
                        )}
                      </ul>

                      {/* Bouton CTA */}
                      <Button 
                        className={`w-full mt-4 ${
                          isPremium 
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                            : isPro
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : ''
                        }`}
                        variant={isPro || isPremium ? 'default' : 'outline'}
                        onClick={() => handleSubscribe(planId)}
                        disabled={isActive}
                      >
                        {isActive ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Abonnement actif
                          </>
                        ) : (
                          <>
                            Souscrire maintenant
                            <ChevronRight className="h-4 w-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
          </div>

          <MemberComparisonTable />
        </TabsContent>

        {/* Onglet Conseillers */}
        <TabsContent value="conseiller" className="space-y-8 mt-6">
          <div className="text-center mb-6">
            <Badge className="bg-gradient-to-r from-green-600 to-teal-600 text-white font-semibold">
              Offres Conseillers ESS • COPPAM / FOGEDOM
            </Badge>
          </div>

          {/* Grille des Plans Conseillers */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {plans?.conseiller_plans && Object.entries(plans.conseiller_plans).map(([planId, plan]) => {
              const isFree = plan.price === 0;
              const isEssPlus = planId.includes('ess_plus') || planId.includes('ess+');
              const isPro = planId.includes('ess_pro');
              const isActive = isPlanActive(planId);
              
              return (
                <Card 
                  key={planId}
                  className={`relative transition-all hover:shadow-xl ${
                    isFree ? 'border-gray-300' : ''
                  } ${
                    isEssPlus ? 'border-green-500 border-2 shadow-lg' : ''
                  } ${
                    isPro ? 'border-teal-500 border-2 shadow-xl transform scale-105 bg-gradient-to-br from-teal-50 to-green-50' : ''
                  } ${
                    isActive ? 'ring-2 ring-green-500' : ''
                  }`}
                >
                  {isPro && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <Badge className="bg-gradient-to-r from-teal-600 to-green-600 text-white shadow-md font-semibold">⭐ RECOMMANDÉ PRO</Badge>
                    </div>
                  )}
                  {isActive && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-green-600 text-white font-semibold">✓ Actif</Badge>
                    </div>
                  )}
                  
                  <CardHeader className="pb-4">
                    <div className="space-y-1">
                      <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
                      {plan.target && (
                        <p className="text-xs text-gray-500 font-medium">{plan.target}</p>
                      )}
                    </div>
                    <div className="pt-4">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-4xl font-bold ${isFree ? 'text-gray-600' : 'text-teal-600'}`}>
                          {plan.price}€
                        </span>
                        <span className="text-gray-500">/mois</span>
                      </div>
                      {isFree && (
                        <Badge className="mt-2 bg-green-700 text-white font-semibold">100% Gratuit</Badge>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Bloc Crédits Offerts */}
                    {plan.monthly_credits > 0 && (
                      <div className={`p-4 rounded-lg border-2 ${
                        isPro 
                          ? 'bg-gradient-to-br from-teal-100 to-green-100 border-teal-300'
                          : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Heart className="h-5 w-5 text-red-500 fill-red-500" />
                          <span className="font-bold text-sm">Bonus Mensuel</span>
                        </div>
                        <p className="text-2xl font-bold text-teal-900">
                          {plan.monthly_credits} crédits
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Valeur : {plan.monthly_credits}€/mois
                        </p>
                        {isPro && (
                          <div className="mt-2 pt-2 border-t border-teal-200">
                            <p className="text-xs font-semibold text-green-700">
                              💡 Coût net réel : {plan.price - plan.monthly_credits}€/mois
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Liste des Fonctionnalités */}
                    <ul className="space-y-2.5">
                      {plan.features && plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Bouton CTA */}
                    <Button 
                      className={`w-full mt-4 ${
                        isPro 
                          ? 'bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700'
                          : isEssPlus
                          ? 'bg-green-600 hover:bg-green-700'
                          : ''
                      }`}
                      variant={isEssPlus || isPro ? 'default' : 'outline'}
                      onClick={() => handleSubscribe(planId)}
                      disabled={isActive}
                    >
                      {isActive ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Abonnement actif
                        </>
                      ) : isFree ? (
                        <>
                          Activer gratuitement
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </>
                      ) : (
                        <>
                          Souscrire maintenant
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <ConseillerComparisonTable />
        </TabsContent>
      </Tabs>

      {/* Section Informations Supplémentaires */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            Informations Importantes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-700">
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p><strong>Annulation :</strong> Possible à tout moment, prend effet à la fin de la période payée</p>
          </div>
          <div className="flex items-start gap-2">
            <CreditCard className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p><strong>Paiement :</strong> CB, virement SEPA, crypto (USDT/USDC/MATIC)</p>
          </div>
          <div className="flex items-start gap-2">
            <Heart className="h-5 w-5 text-red-500 fill-red-500 flex-shrink-0 mt-0.5" />
            <p><strong>Crédits :</strong> Les crédits offerts se renouvellent chaque mois, les crédits achetés n'expirent jamais</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManagement;
